/**
 * LeftNav — 좌측 공통 사이드바 (Shell 고정)
 *
 * 소유: 프레임 팀. 에이전트 수정 금지.
 * 메뉴 항목 추가·수정: 코드가 아니라 TM_MENU(WEB_STUDIO) 데이터로 함 — registry/menu-tree.ts
 * 참고. 에이전트 소속 판정만 registry/nav-mapping.ts / menu-config.ts 를 본다.
 *
 * 사이드바 모드 (상세기능정의서 C-1):
 *   expanded  — 전체 너비(w-60). 텍스트 + 아이콘 표시.
 *   rail      — 아이콘만(w-14). 버튼 클릭 시 바로 이동.
 *
 * 스튜디오 개선 설계서(2026-09) FR-01 — 좌측 내비 정적 고정화: 예전엔 에이전트
 * 라우트(/agents/*) 안에 들어가면 좌측 메뉴 전체가 그 에이전트의 하위 메뉴(생성/관리/
 * 공유/설정 등)로 바뀌었다(activeAgent 분기). 이제는 어느 화면에 있든 좌측 메뉴가
 * 항상 고정 트리 그대로다 — 에이전트 내부 탐색은 각 에이전트 자체 UI가 담당.
 *
 * 스튜디오_개선_설계서_v3.0 §9 FR-27~30 — GET /indipia/menus?portalCode=WEB_STUDIO(TM_MENU)
 * 조회 결과를 그대로 렌더한다(useMenuTree). 마스터 전용 항목(직원관리) 필터링도 서버가 이미
 * 걸러 내려주므로(FR-28) 이 파일은 masterYn 분기를 두지 않는다. 조회 실패 시 최소 구성
 * (스튜디오 홈만)으로 폴백한다.
 *
 * 어디에 무엇을 그릴지는 메뉴 코드가 아니라 TM_MENU 값만으로 정한다 — 메뉴 추가·이동·순서
 * 변경은 DB만 고치면 된다.
 *   MENU_POSITION_CODE  LEFT_TOP     — 상단 고정 영역(그룹 외 항목) + 스크롤 그룹 영역(GROUP_HEADER)
 *                       LEFT_BOTTOM  — 하단 고정 영역. 연속된 LINK는 한 블록, FLYOUT_ROOT는 각자
 *                                      한 블록(블록 사이 구분선). 사용자 카드(USER_CARD_CODE)는 맨 아래.
 *                       RIGHT_TOP    — 여기선 안 그림(TopBar.tsx 드롭다운).
 *   MENU_TYPE_CODE      LINK         — routePath로 이동
 *                       FLYOUT_ROOT  — 자식 메뉴를 서브 패널로 연다(하위 COLLAPSE_GROUP/GROUP_HEADER 지원).
 *                                      LINK_TARGET_CODE=FLYOUT_OPEN이어도 동일.
 *                       GROUP_HEADER — 그룹 제목 + 자식 목록
 *   SORT_ORDER          — 같은 위치 안의 순서
 *   ICON_KEY            — 메뉴 아이콘(아래 MenuIcon 참고)
 * 코드로 남은 것은 메뉴 구조가 아닌 "기능" 표시뿐이다:
 * 사용자 카드(아바타·이름), 안 읽은 알림 배지, 현장 등록 유도 말풍선, 로그아웃 강조색.
 *
 * 서브 패널 (LEFT_* 의 FLYOUT_ROOT 공통):
 *   핀 눌림   — 레이아웃에 포함(콘텐츠가 우측으로 밀림)
 *   핀 안 눌림 — absolute, 콘텐츠 위에 겹침. 바깥 클릭 시 닫힘.
 */
import { useState, useEffect, useRef } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { Skeleton } from '@indipia/ui-core';
import { useAuthStore, useAppStore } from '@indipia/shared';
import { fetchSiteList } from '../../shared/api/sites';
import { useTodos } from '../../shared/todo/useTodos';
import { resolveNavOwner } from '../../registry/nav-mapping';
import {
  useMenuTree, pickByPosition, findByCode, toVisibleTree,
  isExactRoute, isRouteOrDescendant, hasActiveDescendant, resolveMenuIconUrl, DEFAULT_MENU_ICON_URL,
  type MenuNode as TreeMenuNode,
} from '../../registry/menu-tree';
import { useNotifications } from './notifications/NotificationProvider';
import { useSitePanel } from './SitePanelContext';

// 조회 실패 시 최소 구성 폴백(§4.2 "로딩·실패 처리") — 스튜디오 홈만 노출.
const FALLBACK_TREE: TreeMenuNode[] = [
  { menuId: 0, parentMenuId: null, positionCode: 'LEFT_TOP', typeCode: 'LINK', linkTarget: 'SCREEN_PUSH', code: 'STUDIO_HOME', name: '스튜디오 홈', iconKey: 'home', routePath: '/app', sortOrder: 10, level: 1, displayYn: 'Y', children: [] },
];

// ── 메뉴 구조와 무관한 "기능" 표시용 코드 ─────────────────────────────────────
// 위치·순서·소속은 전부 DB가 정하고, 아래 코드는 "그 메뉴가 어디에 있든" 붙는 표시만 결정한다.
/** 사용자 카드(아바타 + "{이름}님")로 그리는 메뉴 — LEFT_BOTTOM 맨 아래 고정, 소속 대기 직원에게도 노출 */
const USER_CARD_CODE = 'PROFILE';
/** 안 읽은 알림 수 배지를 붙이는 메뉴 */
const UNREAD_BADGE_CODE = 'NOTIFICATION';
/** 나의 할일 숫자 배지를 붙이는 메뉴 */
const TODO_BADGE_CODE = 'MY_TODAY_TODO';
/** 사용자 카드 활성 판정에 추가로 포함할 경로 — TM_MENU에 없는 탈퇴 화면(FR-10 세부기능) */
const USER_CARD_EXTRA_PREFIXES = ['/settings/withdraw'];

interface SubPanelState {
  code: string;
  pinned: boolean;
  /** 고정 안 된 패널의 세로 위치 — LEFT_TOP 항목에서 열면 그 행의 top(사이드바 기준 px)에 맞춰
   *  우측에 띄우고, null(LEFT_BOTTOM 항목 등)이면 사이드바 하단에 붙여 아래에서부터 그린다. */
  anchorTop: number | null;
}

// 1depth(다크 사이드바) — Figma NavBtn: px 8 · py 16 · gap 8 · Bold 16 · 선택 시 rounded 8 배경
const navRowCls   = 'flex w-full items-center gap-2 rounded-lg px-2 text-base font-bold leading-none text-lnb-fg transition-colors duration-200';
const baseLinkCls = `${navRowCls} py-4`;
// 그룹 하위 항목(현장 파트너·작업 보관함)만 상하 12 — Figma Lnb(4149:2598) 행 높이 52→44
const groupLinkCls = `${navRowCls} py-3`;
const activeCls   = 'bg-lnb-active text-lnb-activeFg';
const inactiveCls = 'hover:bg-lnb-hover';
// 2depth 서브 패널(흰 배경) — Figma "2 depth - 3 depth": 행 높이 40, 좌 24 / 3depth 좌 32
const subLinkCls     = 'flex h-08 w-full items-center gap-2 pl-6 pr-2 text-sm leading-snug text-lnb-subFg hover:bg-lnb-subActive hover:font-bold transition-colors duration-200';
const subActiveCls   = 'bg-lnb-subActive font-bold';
const sub3LinkCls    = 'flex w-full items-center bg-lnb-subActive py-2 pl-8 pr-2 text-xs leading-none text-lnb-subFg hover:font-bold transition-colors duration-200';
const railIconCls = 'flex h-10 w-10 items-center justify-center rounded-md text-lnb-muted hover:bg-lnb-hover transition-colors duration-200';
const railIconActCls = 'flex h-10 w-10 items-center justify-center rounded-md bg-lnb-active text-lnb-activeFg transition-colors duration-200';

// ── 아이콘 — Figma Lnb(3552:1978) mingcute/solar 에셋, public/icons/lnb/*.svg ──────────
// 화면 장치(핀·닫기·화살표 등) 전용. 메뉴 아이콘은 TM_MENU.ICON_KEY 기반 MenuIcon을 쓴다.
// 흰색(또는 지정색) fill이 박혀 있는 SVG라 <img>로 쓴다. size는 SVG 루트 크기와 동일하게 준다.
function Ic({ name, size = 20, className = '' }: { name: string; size?: number; className?: string }) {
  return <img src={`/icons/lnb/${name}.svg`} alt="" width={size} height={size} className={`shrink-0 ${className}`} />;
}

/**
 * 메뉴 아이콘(TM_MENU.ICON_KEY) — LeftNav·TopBar 공용.
 *   기본  — SVG에 박힌 색(다크 사이드바용 흰색) 그대로, 크기는 SVG 자체 크기.
 *   tint  — CSS mask로 모양만 쓰고 색은 글자색(currentColor)을 따른다(밝은 배경의 상단바용).
 *   dot   — 우상단 빨간 점(안 읽은 알림 등).
 * ICON_KEY가 비었거나 파일을 못 불러오면 기본 아이콘(/icons/lnb/default.svg)을 그린다.
 */
export function MenuIcon({
  iconKey, size, tint = false, dot = false,
}: { iconKey: string | null | undefined; size?: number; tint?: boolean; dot?: boolean }) {
  const resolved = resolveMenuIconUrl(iconKey);
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [resolved]);
  const url = failed ? DEFAULT_MENU_ICON_URL : resolved;
  const px = size ?? 20;

  // 기본 아이콘 자체가 실패하면 더 대체하지 않는다(무한 재시도 방지)
  const onError = () => { if (url !== DEFAULT_MENU_ICON_URL) setFailed(true); };

  const icon = tint ? (
    <span
      aria-hidden
      className="inline-block shrink-0"
      style={{ width: px, height: px, backgroundColor: 'currentColor', WebkitMask: `url("${url}") center / contain no-repeat`, mask: `url("${url}") center / contain no-repeat` }}
    >
      {/* mask는 로드 실패를 알 수 없어서, 숨긴 img로 같은 파일을 불러 실패를 감지한다 */}
      <img src={url} alt="" hidden onError={onError} />
    </span>
  ) : (
    <img src={url} alt="" width={size} height={size} className="shrink-0" onError={onError} />
  );

  if (!dot) return icon;
  return (
    <span className="relative inline-flex shrink-0">
      {icon}
      <span className="absolute -right-0.5 -top-0.5 h-1.5 w-1.5 rounded-full bg-error" />
    </span>
  );
}

const IcChevronRight = () => <Ic name="chevron-right" size={16} className="-rotate-90" />;

/** LEFT_BOTTOM 하단 고정 영역 블록 — 연속된 이동형(LINK)은 한 블록으로 묶고, 서브 패널형은 각자 한 블록. */
/**
 * 서브 패널을 여는 항목인지 — MENU_TYPE_CODE=FLYOUT_ROOT 또는 LINK_TARGET_CODE=FLYOUT_OPEN.
 * 둘 다 아니어도 경로 없이 하위 메뉴만 있는 항목은 이동할 곳이 없으므로 서브 패널로 연다.
 */
const isFlyout = (n: TreeMenuNode) =>
  n.typeCode === 'FLYOUT_ROOT' || n.linkTarget === 'FLYOUT_OPEN'
  || (n.typeCode !== 'GROUP_HEADER' && !n.routePath && n.children.length > 0);

function toBottomBlocks(nodes: TreeMenuNode[]): TreeMenuNode[][] {
  const blocks: TreeMenuNode[][] = [];
  for (const n of nodes) {
    const last = blocks[blocks.length - 1];
    if (!isFlyout(n) && last && !isFlyout(last[0])) last.push(n);
    else blocks.push([n]);
  }
  return blocks;
}

// ── 레일 아이콘 버튼 ────────────────────────────────────────────────────────

function RailBtn({
  children, label, active, onClick,
}: {
  children: React.ReactNode;
  label: string;
  active?: boolean;
  onClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
}) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      onClick={onClick}
      className={active ? railIconActCls : railIconCls}
    >
      {children}
    </button>
  );
}

function UnreadBadge({ count }: { count: number }) {
  if (count <= 0) return null;
  return (
    <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-error px-1.5 text-xs font-medium leading-none text-white">
      {count > 99 ? '99+' : count}
    </span>
  );
}

// ── 메인 컴포넌트 ──────────────────────────────────────────────────────────

export function LeftNav() {
  const user    = useAuthStore((s) => s.user);
  const initial = user?.memberName?.trim().charAt(0) || 'U';
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const { close: closeSitePanel } = useSitePanel();

  const initLoaded = useAppStore((s) => s.initLoaded);
  const session     = useAppStore((s) => s.session);
  // 소속 대기(PENDING)·소속 없음(REJECTED·RELEASED·미신청) 직원 — LNB를 상단 고정 항목(스튜디오 홈)과
  // 사용자 카드만 노출하도록 축소 (RIGHT_TOP 메뉴는 TopBar에 있어 이 축소와 무관)
  const isRestrictedStaff = session?.masterYn === 'N'
    && (session?.affiliationStatus === 'PENDING' || !session?.compId);

  const { data: menuTree, isLoading: menuLoading, isError: menuError } = useMenuTree();
  // 렌더링은 displayYn='N'(상세/드릴다운 등 라우팅 전용) 노드를 걷어낸 tree로,
  // 활성 판정은 그 노드들까지 포함한 원본(rawTree)으로 한다 — 상세 화면에서도 메뉴 활성 유지.
  const rawTree = menuError ? FALLBACK_TREE : (menuTree ?? []);
  const tree = toVisibleTree(rawTree);
  const leftTopNodes = pickByPosition(tree, 'LEFT_TOP');
  const leftBottomNodes = pickByPosition(tree, 'LEFT_BOTTOM');
  const userCardNode = leftBottomNodes.find((n) => n.code === USER_CARD_CODE) ?? null;
  // 상단 고정 영역: LEFT_TOP 중 그룹이 아닌 항목(스튜디오 홈 등) / 스크롤 영역: GROUP_HEADER
  const topFixedNodes = leftTopNodes.filter((n) => n.typeCode !== 'GROUP_HEADER');
  const groupNodes = leftTopNodes.filter((n) => n.typeCode === 'GROUP_HEADER');
  const bottomBlocks = toBottomBlocks(leftBottomNodes.filter((n) => n !== userCardNode));
  // 서브 패널을 여는 좌측 노드 전체(위치 무관) — 패널 높이·활성 동기화 대상
  const flyoutNodes = [...leftTopNodes, ...leftBottomNodes].filter(isFlyout);

  // 등록된 현장이 0개면 "나의 현장" 메뉴에 등록 유도 말풍선 표시
  // 'site-list' 접두어를 공유해 현장 등록/변경 시 invalidateQueries(['site-list'])로 같이 무효화되게 함
  const { data: siteListData } = useQuery({
    queryKey: ['site-list', 'count'],
    queryFn: () => fetchSiteList({}),
    enabled: !isRestrictedStaff,
    staleTime: 60_000,
  });
  const hasNoSites = initLoaded && (siteListData?.total ?? 0) === 0;
  const { unreadCount } = useNotifications();
  // 나의 할일 배지 — 오늘 할일(빨강·노랑) 건수. 조회 오류 시에는 숫자를 만들지 않고 배지를 숨긴다(오류 표시는 할일 화면).
  const { data: todosData } = useTodos({ enabled: !isRestrictedStaff });
  const todoCount = todosData ? todosData.summary.pendingCount + todosData.summary.todayCount : 0;
  const badgeOf = (n: TreeMenuNode) => {
    if (n.code === UNREAD_BADGE_CODE) return unreadCount;
    if (n.code === TODO_BADGE_CODE) return todoCount;
    return 0;
  };

  const owner = resolveNavOwner(pathname);

  /** 현재 위치와 가장 가깝게 맞는 메뉴 경로 — /agents/portfolio/write 에서 상위 경로 /agents/portfolio 메뉴까지 켜지지 않게 */
  const bestRoute = (() => {
    let best: string | null = null;
    const walk = (nodes: TreeMenuNode[]) => nodes.forEach((m) => {
      if (m.routePath && isRouteOrDescendant(m.routePath, pathname, search) && (!best || m.routePath.length > best.length)) best = m.routePath;
      walk(m.children);
    });
    walk(rawTree ?? []);
    return best as string | null;
  })();

  /** 이동형 항목(LINK) 활성 — 자기 경로(또는 하위 경로, 더 가깝게 맞는 메뉴가 있으면 그 메뉴만)이거나, 같은 에이전트 소속 화면(nav-mapping) */
  const isLinkActive = (n: TreeMenuNode) => {
    if (!n.routePath) return false;
    if (bestRoute) return n.routePath === bestRoute;
    const linkOwner = resolveNavOwner(n.routePath.split('?')[0]);
    return !!owner && !!linkOwner && owner.ownerId === linkOwner.ownerId;
  };
  /** 접이식 그룹(COLLAPSE_GROUP) 활성 — 하위 메뉴 화면 또는 그 세부 화면(하위 경로)에 있으면 활성 */
  const isCollapseGroupActive = (g: TreeMenuNode) =>
    (findByCode(rawTree, g.code)?.children ?? []).some((s) => s.routePath && isRouteOrDescendant(s.routePath, pathname, search));
  /** 서브 패널형 항목 활성 — 원본 트리 기준 자식·손자 중 현재 화면이 있으면 활성 */
  const isFlyoutActive = (n: TreeMenuNode) => {
    const raw = findByCode(rawTree, n.code);
    if (hasActiveDescendant(raw, pathname, search)) return true;
    if (raw?.children.some((c) => c.typeCode === 'COLLAPSE_GROUP' && isCollapseGroupActive(c))) return true;
    return n.code === USER_CARD_CODE && USER_CARD_EXTRA_PREFIXES.some((p) => pathname.startsWith(p));
  };
  const activeFlyoutCode = flyoutNodes.find(isFlyoutActive)?.code ?? null;

  const [sidebarMode, setSidebarMode] = useState<'expanded' | 'rail'>('expanded');
  // LNB 열림/닫힘을 <html data-lnb> 로 노출 — 목록 카드 그리드(.card-list.cols-1-2-3-4)가 남는 폭에 맞춰 열 수를 바꾼다
  useEffect(() => {
    document.documentElement.dataset.lnb = sidebarMode;
    return () => { delete document.documentElement.dataset.lnb; };
  }, [sidebarMode]);
  const [subPanel, setSubPanel]       = useState<SubPanelState | null>(null);

  // 라우트 변경 시 현장 패널 닫기 — 고객현장 등록 버튼은 navigate 없으므로 자동 예외
  useEffect(() => {
    closeSitePanel();
    // 서브 패널형 메뉴의 화면 밖으로 이동하면 열린(고정 안 된) 서브 패널을 닫는다.
    if (!activeFlyoutCode) {
      setSubPanel((v) => (v && !v.pinned ? null : v));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, search]);

  // COLLAPSE_GROUP(접이식 그룹, 예: 사용자 정보 플라이아웃의 "구독·결제") 펼침 상태.
  // 그 안의 화면에 있을 때는 항상 펼쳐지도록 유지한다.
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  useEffect(() => {
    const activeGroups = flyoutNodes
      .flatMap((f) => f.children)
      .filter((c) => c.typeCode === 'COLLAPSE_GROUP' && isCollapseGroupActive(c))
      .map((c) => c.code);
    if (activeGroups.length) {
      setExpandedGroups((prev) => (activeGroups.every((c) => prev.has(c)) ? prev : new Set([...prev, ...activeGroups])));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, search, menuTree]);
  // COLLAPSE_GROUP을 펼쳤을 때 하위 메뉴가 패널 아래로 밀려나 스크롤해야만 보이는 문제(사용자
  // 리포트, 2026-09-17) — 펼친 직후 그룹 전체가 보이도록 스크롤해준다. 접을 때는 스크롤을 건드리지
  // 않는다(닫히면서 내용이 줄어들 뿐 안 보이는 문제 자체가 없음).
  const groupRefs = useRef<Map<string, HTMLLIElement>>(new Map());
  const toggleGroup = (code: string) =>
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(code)) {
        next.delete(code);
      } else {
        next.add(code);
        // state 반영(리렌더로 하위 메뉴 DOM 추가)이 끝난 다음 프레임에 스크롤 — 그래야 늘어난
        // 높이를 기준으로 scrollIntoView가 올바르게 계산된다.
        requestAnimationFrame(() => {
          groupRefs.current.get(code)?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        });
      }
      return next;
    });

  // 서브 패널이 이미 열려 있는 상태에서 다른 서브 패널 메뉴의 화면으로 이동할 때만 타입을 맞춰준다.
  // 패널이 닫혀 있을 때(알림 "바로가기" 등 외부 진입)는 강제로 열지 않는다 — 콘텐츠를 가리는 걸 방지.
  useEffect(() => {
    // 다른 메뉴의 패널로 바뀌면 기준 행을 알 수 없으므로 하단 정렬로 되돌린다.
    if (activeFlyoutCode) setSubPanel((v) => (v && v.code !== activeFlyoutCode ? { ...v, code: activeFlyoutCode, anchorTop: null } : v));
  }, [activeFlyoutCode]);

  const asideRef    = useRef<HTMLElement>(null);
  const subPanelRef = useRef<HTMLDivElement>(null);

  // 서브 패널 바깥 클릭 시 닫기 (pinned 아닐 때만)
  useEffect(() => {
    if (!subPanel || subPanel.pinned) return;
    const handler = (e: MouseEvent) => {
      const t = e.target as Node;
      if (!asideRef.current?.contains(t) && !subPanelRef.current?.contains(t)) {
        setSubPanel(null);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [subPanel]);

  const activeSubNode = subPanel ? findByCode(tree, subPanel.code) : null;
  const subMenuItems  = activeSubNode?.children ?? [];
  // 고정 안 된 서브 패널 기본 높이 — 서브 패널형 메뉴 중 하위 메뉴가 가장 많은 패널 높이로 통일
  // (패널을 바꿔 열어도 높이가 들쭉날쭉하지 않게). 접이식 그룹은 접힌 상태 기준,
  // 펼치면 min-height라 그만큼 더 늘어난다. 행 높이: 헤더·일반 행 40, GROUP_HEADER 28(pt-3+pb-1+12).
  const panelHeight = (node: TreeMenuNode) =>
    40 + node.children.reduce((h, c) => h + (c.typeCode === 'GROUP_HEADER' ? 28 : 40), 0);
  const subPanelMinH = Math.max(0, ...flyoutNodes.map(panelHeight));

  const subPanelTitle = subPanel?.code === USER_CARD_CODE
    ? `${user?.memberName ?? '사용자'}님`
    : (activeSubNode?.name ?? '');

  const outerRef = useRef<HTMLDivElement>(null);
  // anchorEl(클릭한 행)을 주면 그 행과 같은 높이에 패널을 띄운다(LEFT_TOP). 없으면 하단 정렬(LEFT_BOTTOM).
  const openSubPanel = (code: string, anchorEl?: HTMLElement) => {
    const base = outerRef.current?.getBoundingClientRect().top;
    const anchorTop = anchorEl && base !== undefined ? anchorEl.getBoundingClientRect().top - base : null;
    setSubPanel((v) => (v?.code === code ? null : { code, pinned: v?.pinned ?? false, anchorTop }));
  };

  // 항목 클릭 — 서브 패널형은 패널 열기, 그 외는 routePath로 이동
  const onItemClick = (n: TreeMenuNode, anchorEl?: HTMLElement) => {
    if (isFlyout(n)) openSubPanel(n.code, anchorEl);
    else if (n.routePath) navigate(n.routePath);
  };
  const isItemActive = (n: TreeMenuNode) =>
    isFlyout(n) ? isFlyoutActive(n) || subPanel?.code === n.code : isLinkActive(n);

  // 서브 패널의 absolute 위치 (rail / expanded 에 따라 다름)
  const subPanelAbsLeft = sidebarMode === 'rail' ? 'left-14' : 'left-60';

  // 외부 div 너비
  const outerWidthCls = (() => {
    if (sidebarMode === 'rail') return subPanel?.pinned ? 'w-[280px]' : 'w-14'; // 56+224
    return subPanel?.pinned ? 'w-[464px]' : 'w-60';                              // 240+224
  })();

  // Drawer "크게보기"(FR-13) 등 LeftNav 밖 화면이 실제 폭을 알아야 할 때를 위해 appStore에 발행.
  const setNavWidth = useAppStore((s) => s.setNavWidth);
  useEffect(() => {
    const width = sidebarMode === 'rail'
      ? (subPanel?.pinned ? 280 : 56)
      : (subPanel?.pinned ? 464 : 240);
    setNavWidth(width);
  }, [sidebarMode, subPanel?.pinned, setNavWidth]);

  if (menuLoading) {
    return (
      <div className="flex h-full w-60 shrink-0 flex-col gap-2 border-r border-lnb-border bg-lnb p-3">
        <Skeleton height={40} />
        <Skeleton height={32} />
        <Skeleton height={32} />
        <Skeleton height={32} />
      </div>
    );
  }


  return (
    <div ref={outerRef} className={`relative flex shrink-0 overflow-visible transition-[width] duration-200 ${outerWidthCls}`}>

      {/* ─── RAIL 모드 ─────────────────────────────────────────────────── */}
      {sidebarMode === 'rail' && (
        <aside
          ref={asideRef}
          className="flex h-full w-14 shrink-0 flex-col items-center border-r bg-lnb"
        >
          {/* 워크스페이스 로고 — 클릭 시 랜딩페이지(루트)로 이동 */}
          <button
            type="button"
            title="랜딩페이지로 이동"
            aria-label="랜딩페이지로 이동"
            onClick={() => navigate('/')}
            className="flex h-14 w-full items-center justify-center border-b border-lnb-headerBorder bg-lnb-header"
          >
            <img src="/brand/indipia-symbol.svg" alt="INDIPIA" className="h-8 w-8" />
          </button>

          {/* 펼치기 버튼 */}
          <div className="flex w-full justify-center py-2">
            <button
              type="button"
              title="사이드바 펼치기"
              aria-label="사이드바 펼치기"
              onClick={() => setSidebarMode('expanded')}
              className="flex h-7 w-7 items-center justify-center rounded-full bg-point text-white shadow-md transition-transform duration-150 hover:scale-110 hover:bg-primary-600"
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="6 18 12 12 6 6" />
                <polyline points="13 18 19 12 13 6" />
              </svg>
            </button>
          </div>

          {/* 메인 아이콘 — 소속 승인 대기 중인 직원은 LEFT_TOP 상단 고정 항목(스튜디오 홈 등)만 노출 */}
          <nav aria-label="주 메뉴" className="flex flex-1 flex-col items-center gap-1 overflow-y-auto py-1">
            {topFixedNodes.map((item) => (
              <RailBtn key={item.code} label={item.name} active={isItemActive(item)} onClick={(e) => onItemClick(item, e.currentTarget)}>
                <MenuIcon iconKey={item.iconKey} dot={badgeOf(item) > 0} />
              </RailBtn>
            ))}

            {!isRestrictedStaff && groupNodes.map((group) => (
              <div key={group.code} className="contents">
                <div className="my-1 w-8 border-t border-lnb-border" />
                {group.children.map((item) => (
                  <div key={item.code} className="relative">
                    <RailBtn label={item.name} active={isItemActive(item)} onClick={(e) => onItemClick(item, e.currentTarget)}>
                      <MenuIcon iconKey={item.iconKey} dot={badgeOf(item) > 0} />
                    </RailBtn>
                    {item.code === 'MY_SITE' && hasNoSites && (
                      <span className="absolute right-1.5 top-1.5 h-1.5 w-1.5 rounded-full bg-point" />
                    )}
                  </div>
                ))}
              </div>
            ))}

            {!isRestrictedStaff && bottomBlocks.map((block, i) => (
              <div key={i} className="contents">
                <div className="my-1 w-8 border-t border-lnb-border" />
                {block.map((item) => (
                  <RailBtn key={item.code} label={item.name} active={isItemActive(item)} onClick={() => onItemClick(item)}>
                    <MenuIcon iconKey={item.iconKey} dot={badgeOf(item) > 0} />
                  </RailBtn>
                ))}
              </div>
            ))}
          </nav>

          {/* 사용자 카드(아바타) */}
          {userCardNode && (
            <div className="flex w-full justify-center border-t border-lnb-border py-3">
              <button
                type="button"
                title={`${user?.memberName ?? '사용자'}님`}
                aria-label={`${user?.memberName ?? '사용자'}님`}
                onClick={() => onItemClick(userCardNode)}
                className={`flex h-8 w-8 items-center justify-center rounded-full text-xs font-semibold ${
                  isItemActive(userCardNode) ? 'bg-point text-white' : 'bg-point-subtle text-point'
                }`}
              >
                {initial}
              </button>
            </div>
          )}
        </aside>
      )}

      {/* ─── EXPANDED 모드 ─────────────────────────────────────────────── */}
      {sidebarMode === 'expanded' && (
        <aside
          ref={asideRef}
          className="relative flex h-full w-60 shrink-0 flex-col border-r border-lnb-border bg-lnb"
        >
          {/* 접기 버튼 */}
          <button
            type="button"
            title="아이콘 레일로 접기"
            aria-label="아이콘 레일로 접기"
            onClick={() => setSidebarMode('rail')}
            className="absolute right-0 top-16 z-20 flex h-7 w-7 translate-x-1/2 items-center justify-center rounded-full bg-point text-white shadow-md transition-transform duration-150 hover:scale-110 hover:bg-primary-600"
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="18 18 12 12 18 6" />
              <polyline points="11 18 5 12 11 6" />
            </svg>
          </button>

          {/* ① 워크스페이스 로고 — 클릭 시 랜딩페이지(루트)로 이동 */}
          <button
            type="button"
            title="랜딩페이지로 이동"
            aria-label="랜딩페이지로 이동"
            onClick={() => navigate('/')}
            className="flex h-14 shrink-0 items-center border-b border-lnb-headerBorder bg-lnb-header px-4"
          >
            <img src="/brand/indipia-logo.svg" alt="INDIPIA" className="w-auto" />
          </button>

          {/* ② 메인 메뉴 — 소속 승인 대기 중인 직원은 LEFT_TOP 상단 고정 항목(스튜디오 홈 등)만 노출(그 화면 자체가 웰컴/대기 콘텐츠로 대체됨) */}
          {/* min-h-0: flex 자식 기본 min-height:auto 때문에 메뉴가 길면 nav가 줄지 않고 하단 고정 메뉴를 화면 밖으로 밀어냈다.
              상단 고정 항목은 고정, 그 아래 그룹 영역(GROUP_HEADER)만 스크롤한다. */}
          <nav aria-label="주 메뉴" className="flex min-h-0 flex-1 flex-col px-4 pt-4">
            <ul>
              {topFixedNodes.map((item) => (
                <li key={item.code}>
                  <button
                    type="button"
                    onClick={(e) => onItemClick(item, e.currentTarget)}
                    className={`${baseLinkCls} ${isItemActive(item) ? activeCls : inactiveCls}`}
                  >
                    <MenuIcon iconKey={item.iconKey} />
                    <span className="flex-1 text-left">{item.name}</span>
                    <UnreadBadge count={badgeOf(item)} />
                    {isFlyout(item) && item.children.length > 0 && <IcChevronRight />}
                  </button>
                </li>
              ))}
            </ul>
            <div className="min-h-0 flex-1 overflow-y-auto [scrollbar-color:var(--lnb-border)_transparent] [scrollbar-width:thin]">

            {!isRestrictedStaff && groupNodes.map((group) => (
              <div key={group.code}>
                <div className="border-b border-lnb-border pb-2 pt-4 text-xs leading-none text-lnb-fg">{group.name}</div>
                <ul>
                  {group.children.map((item) => (
                    <li key={item.code} className="relative">
                      <button
                        type="button"
                        onClick={(e) => onItemClick(item, e.currentTarget)}
                        className={`${groupLinkCls} ${isItemActive(item) ? activeCls : inactiveCls}`}
                      >
                        <MenuIcon iconKey={item.iconKey} />
                        <span className="flex-1 text-left">{item.name}</span>
                        <UnreadBadge count={badgeOf(item)} />
                        {item.children.length > 0 && <IcChevronRight />}
                      </button>
                      {item.code === 'MY_SITE' && hasNoSites && (
                        // nav가 overflow-y-auto라 가로로 튀어나오면(left-full 등) overflow-x까지
                        // auto로 강제되어 사이드바에 원치 않는 가로 스크롤바가 생긴다(CSS overflow
                        // 스펙 — 한 축만 visible이 아니면 다른 축의 visible도 auto로 취급됨).
                        // 버튼 오른쪽 밖으로 나가지 않도록 같은 행 안(오른쪽 끝)에 겹쳐 띄운다.
                        <div className="pointer-events-none absolute right-1.5 top-1/2 z-10 max-w-[128px] -translate-y-1/2 rounded-lg bg-point px-2 py-1 text-caption font-semibold leading-tight text-white shadow-md">
                          현장을 등록해보세요
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
            </div>
          </nav>

          {/* ③ 하단 고정 메뉴(LEFT_BOTTOM) — SORT_ORDER 순. 연속된 LINK는 한 블록, 서브 패널형은 각자 한 블록.
              소속 승인 대기 중인 직원은 숨김 */}
          {!isRestrictedStaff && bottomBlocks.map((block, i) => (
            <div key={i} className="shrink-0 border-t border-lnb-border px-4 py-2">
              <ul>
                {block.map((item) => (
                  <li key={item.code}>
                    <button
                      type="button"
                      onClick={() => onItemClick(item)}
                      className={`${baseLinkCls} ${isItemActive(item) ? activeCls : inactiveCls}`}
                    >
                      <MenuIcon iconKey={item.iconKey} />
                      <span className="flex-1 text-left">{item.name}</span>
                      <UnreadBadge count={badgeOf(item)} />
                      {item.children.length > 0 && <IcChevronRight />}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ))}

          {/* ④ 사용자 카드 — LEFT_BOTTOM의 USER_CARD_CODE 메뉴. 맨 아래 고정 */}
          {userCardNode && (
            <button
              type="button"
              onClick={() => onItemClick(userCardNode)}
              className={`flex w-full shrink-0 items-center gap-2 pb-4 pl-4 pr-6 pt-3 hover:bg-lnb-hover transition-colors duration-200 ${
                isFlyoutActive(userCardNode) ? 'bg-lnb-active' : ''
              }`}
            >
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white text-sm font-bold text-point shadow-sm">
                {initial}
              </span>
              <span className="flex-1 truncate text-left text-base font-bold leading-none text-lnb-fg">{user?.memberName ?? '사용자'}님</span>
              {userCardNode.children.length > 0 && <IcChevronRight />}
            </button>
          )}
        </aside>
      )}

      {/* ─── 서브 패널 ─────────────────────────────────────────────────── */}
      {subPanel && (
        <div
          ref={subPanelRef}
          style={
            subPanel.pinned
              ? undefined
              : subPanel.anchorTop !== null
                // LEFT_TOP 항목: 클릭한 행과 같은 높이에서 시작, 사이드바 아래로 넘치지 않게 남은 높이로 상한
                ? { top: subPanel.anchorTop, maxHeight: `calc(100% - ${subPanel.anchorTop}px)` }
                : { minHeight: `min(${subPanelMinH}px, 100%)` } // min-height가 max-height를 이기므로 100% 상한을 직접 건다
          }
          className={`flex w-56 shrink-0 flex-col overflow-hidden bg-lnb-subBg ${
            subPanel.pinned
              ? 'relative h-full'
              // 고정 h-[320px]였던 걸 max-h-full로 바꿈(2026-09-18, 사용자 리포트) — "구독결제"
              // 그룹을 펼치면 하위 메뉴 4개가 320px를 넘어서 패널 안(nav)에서 스크롤이 생겼다.
              // 원하는 동작은 스크롤이 아니라 패널 자체가 내용만큼 늘어나는 것 — 높이를 고정하지
              // 않고 내용에 맞게 커지되(h-auto, 아래 flex 자식들의 자연스러운 높이 합), 이 패널의
              // 포지셔닝 부모(위 relative 감싸는 div, 사이드바 전체 높이)를 넘지는 않게
              // max-h-full로 상한만 둔다. 그 상한에 실제로 걸리는 극단적인 경우에만 nav의
              // overflow-y-auto(아래)가 안전망으로 스크롤을 대신한다.
              : subPanel.anchorTop !== null
                ? `absolute ${subPanelAbsLeft} z-30 rounded-r-lg shadow-md`
                : `absolute ${subPanelAbsLeft} bottom-0 z-30 max-h-full rounded-r-lg shadow-md`
          }`}
        >
          {/* 헤더 */}
          <div className="flex h-08 shrink-0 items-center gap-2 border-b border-lnb-subBorder pl-4 pr-2">
            <span className="flex-1 truncate text-base font-bold leading-none text-lnb-subFg">{subPanelTitle}</span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                title={subPanel.pinned ? '고정 해제' : '패널 고정'}
                aria-label={subPanel.pinned ? '고정 해제' : '패널 고정'}
                onClick={() => setSubPanel((v) => v ? { ...v, pinned: !v.pinned } : null)}
                className={`transition-opacity ${subPanel.pinned ? '' : 'opacity-40 grayscale hover:opacity-70'}`}
              >
                <Ic name="pin" size={16} />
              </button>
              <button
                type="button"
                title="닫기"
                aria-label="닫기"
                onClick={() => setSubPanel(null)}
                className="opacity-80 hover:opacity-100"
              >
                <Ic name="close" size={16} />
              </button>
            </div>
          </div>

          {/* 메뉴 — 스크롤은 유지하되 스크롤바는 숨김(폭 좁은 서브패널에서 시각적 잡음 방지) */}
          <style>{`[data-hide-scrollbar]::-webkit-scrollbar { display: none; }`}</style>
          <nav
            aria-label="하위 메뉴"
            data-hide-scrollbar
            className="flex-1 overflow-y-auto"
            style={{ scrollbarWidth: 'none' }}
          >
            <ul>
              {subMenuItems.map((item) => {
                if (item.typeCode === 'GROUP_HEADER') {
                  return (
                    <li key={item.code}>
                      <div className="pb-1 pl-4 pt-3 text-xs leading-none text-lnb-subFg opacity-60">{item.name}</div>
                    </li>
                  );
                }
                if (item.typeCode === 'COLLAPSE_GROUP') {
                  const isOpen = expandedGroups.has(item.code);
                  return (
                    <li
                      key={item.code}
                      ref={(el) => {
                        if (el) groupRefs.current.set(item.code, el);
                        else groupRefs.current.delete(item.code);
                      }}
                    >
                      <button
                        type="button"
                        onClick={() => toggleGroup(item.code)}
                        aria-expanded={isOpen}
                        className={`${subLinkCls} ${isOpen ? `${subActiveCls} border-b border-lnb-subBorder` : ''}`}
                      >
                        <span className="flex-1 text-left">{item.name}</span>
                        <Ic name={isOpen ? 'minus' : 'plus'} size={16} />
                      </button>
                      {isOpen && (
                        <ul>
                          {item.children.map((sub) => (
                            <li key={sub.code}>
                              <NavLink
                                to={sub.routePath ?? '#'}
                                className={() => `${sub3LinkCls} ${sub.routePath && isExactRoute(sub.routePath, pathname, search) ? 'font-bold' : ''}`}
                              >
                                {sub.name}
                              </NavLink>
                            </li>
                          ))}
                        </ul>
                      )}
                    </li>
                  );
                }
                return (
                  <li key={item.code}>
                    <NavLink
                      to={item.routePath ?? '#'}
                      className={() =>
                        `${subLinkCls} ${
                          item.routePath && isExactRoute(item.routePath, pathname, search)
                            ? subActiveCls
                            : item.code === 'LOGOUT'
                              ? '!text-error'
                              : ''
                        }`
                      }
                    >
                      {item.name}
                    </NavLink>
                  </li>
                );
              })}
            </ul>
          </nav>
        </div>
      )}
    </div>
  );
}
